import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Link, Paper, Popper, useTheme } from '@mui/material';
import Segmented from './Segmented';
import EntryTypeHelp from './EntryTypeHelp';
import { useEngineStrings } from '../../user_guides/i18n/useEngineStrings';
import { entryTypeOptions, entryTypeCaptionSegments, interpolate, MANAGE_PAYEES_PATH } from '../../utils/entryType';

/**
 * The row's Entry Type control (#1938, DESIGN_SYSTEM.md 8.15): the label, the
 * four-way full-word Segmented, the help icon, and a live caption under them.
 * One panel with the same content from every trigger: the entry's chip, and
 * the payee picker's option chevron (#1943).
 *
 * The caption names what Save writes for the type the ring is on. Hovering or
 * arrow-focusing a segment previews it: the ring moves there and the caption
 * follows; leaving returns both to the type in force (owner, 2026-09-30). The
 * caption comes from `prompt` ({ mode, payeeName, defaultType, value }), the
 * hook's entry or the flyout's per-option prompt; its `{manage}` placeholder
 * renders as a link to the Manage payees page in a new tab.
 *
 * `placement="float"` opens it below the chip (`anchorEl`), left edges aligned,
 * in a Popper kept in place (no portal) with fixed positioning: rendered right
 * after the chip, Tab goes chip, control, next field, and a fixed box adds
 * nothing to the table's scroll width, so the overflow probe cannot flip the
 * page to cards. `placement="side"` opens it beside the picker option
 * (`anchorEl` is that option), right of the list and vertically centered on
 * it, portaled so the list's own box never clips it. `placement="inline"`
 * renders it in flow (the mobile Sheet, cards, and a picker option on phones).
 *
 * It retracts (`onClose`) on a click or a keystroke outside it and on Escape.
 * The anchor, the payee dropdown and the help table count as inside, so a
 * click or a keystroke in another field closes it. The listeners start after
 * the commit that opened it, so the click that committed the payee never
 * closes it. With `keepFocus`, a mousedown inside it is prevented, so the
 * payee field keeps focus and the picker's list stays open while a segment
 * is clicked. With `autoFocus`, the checked segment takes focus on open (the
 * keyboard path from the picker).
 *
 * New payee set to Income (#2288): two buttons appear under the caption,
 * Default and Default + Source of income, and `onProviderChoice` reports the
 * pick. While `pending` the panel is modal: nothing outside it closes it,
 * every pointer or key event outside is stopped, and each attempt runs the
 * flash: the panel's outline pulses twice; on the second pulse the left
 * button pulses three times, then the right button three times, a short gap
 * between pulses. Reduced motion holds one steady outline for the same time.
 *
 * @param {object} props
 * @param {boolean} [props.open]          render or not (float and side: show the Popper)
 * @param {string} props.value            the selected entry type
 * @param {function} [props.onChange]     called with the newly selected value
 * @param {function} [props.onClose]      retract (outside click or key, Escape)
 * @param {object} [props.prompt]         caption inputs; null renders no caption
 * @param {('float'|'side'|'inline')} [props.placement]
 * @param {Element} [props.anchorEl]      the chip or the option: the anchor, and inside for the close rule
 * @param {boolean} [props.keepFocus]     prevent mousedown default inside (picker flyout)
 * @param {boolean} [props.autoFocus]     focus the checked segment when it opens
 * @param {boolean} [props.pending]       #2288: the provider choice is still owed; modal, flashes
 * @param {function} [props.onProviderChoice]  #2288: called with 'all' or 'provider'
 * @param {boolean} [props.incomeAllowed] #2289: false on a Credit Card ledger: no Income option, a note says why
 * @param {object} [props.sx]             extra sx (inline placement only)
 */
// Flash timing (#2288): pulse length, the gap between pulses, and where each
// part starts. The buttons start on the panel's second pulse.
const FLASH_PULSE_MS = 220;
const FLASH_GAP_MS = 120;
const FLASH_STEP_MS = FLASH_PULSE_MS + FLASH_GAP_MS;
const FLASH_LEFT_DELAY_MS = FLASH_STEP_MS;
const FLASH_RIGHT_DELAY_MS = FLASH_LEFT_DELAY_MS + 3 * FLASH_STEP_MS;
const FLASH_TOTAL_MS = FLASH_RIGHT_DELAY_MS + 3 * FLASH_STEP_MS;

const EntryTypeControl = ({
  open = true, value, onChange, onClose, prompt = null, placement = 'inline', anchorEl = null,
  keepFocus = false, autoFocus = false, pending = false, onProviderChoice, incomeAllowed = true, sx,
}) => {
  const t = useEngineStrings();
  const theme = useTheme();
  const { ds, palette } = theme;
  const rootRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const floating = placement === 'float' || placement === 'side';
  const visible = floating ? open && Boolean(anchorEl) : open;
  const shown = preview ?? value;

  // #2288: each outside attempt restarts the flash. Two keyframe names
  // alternate so a change of name restarts the CSS animation; `flashing`
  // holds the reduced-motion outline for the whole sequence.
  const [flashSeq, setFlashSeq] = useState(0);
  const [flashing, setFlashing] = useState(false);
  const flashTimer = useRef(null);
  const flash = useCallback(() => {
    setFlashSeq((n) => n + 1);
    setFlashing(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashing(false), FLASH_TOTAL_MS);
  }, []);
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  useEffect(() => {
    if (!pending) {
      setFlashSeq(0);
      setFlashing(false);
      clearTimeout(flashTimer.current);
    }
  }, [pending]);

  // A fresh open, or a new value in force, starts with no preview.
  useEffect(() => { setPreview(null); }, [visible, value, anchorEl]);

  // The keyboard path: the checked segment takes focus once the panel is in
  // the document. A floating panel mounts a render after it opens (the
  // Popper's portal), so the focus runs from the root's callback ref.
  const focusPendingRef = useRef(false);
  const focusChecked = (node) => node?.querySelector('[role="radio"][aria-checked="true"]')?.focus();
  useEffect(() => {
    focusPendingRef.current = visible && autoFocus;
    if (focusPendingRef.current && rootRef.current) {
      focusChecked(rootRef.current);
      focusPendingRef.current = false;
    }
  }, [visible, autoFocus]);
  const setRoot = useCallback((node) => {
    rootRef.current = node;
    if (node && focusPendingRef.current) {
      focusChecked(node);
      focusPendingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!visible || (!onClose && !pending)) return undefined;
    const inside = (node) => Boolean(node && (
      rootRef.current?.contains(node)
      || (anchorEl && typeof anchorEl.contains === 'function' && anchorEl.contains(node))
      || (typeof node.closest === 'function' && node.closest('.MuiAutocomplete-popper, [role="tooltip"]'))
    ));
    // #2288: while pending, an attempt outside is stopped here, in the
    // capture phase, and answered with the flash.
    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
      flash();
    };
    const onPointer = (e) => {
      if (inside(e.target)) return;
      if (pending) block(e);
      else onClose();
    };
    const onClick = (e) => {
      // A blocked mousedown does not block its click; stop that too, quietly.
      if (pending && !inside(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onKey = (e) => {
      if (inside(e.target)) {
        if (e.key !== 'Escape') return;
        if (pending) {
          e.preventDefault();
          flash();
        } else {
          onClose();
        }
        return;
      }
      if (pending) block(e);
      else onClose();
    };
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('touchstart', onPointer, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer, true);
      document.removeEventListener('touchstart', onPointer, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [visible, onClose, anchorEl, pending, flash]);

  const segments = prompt ? entryTypeCaptionSegments(t, { ...prompt, value: shown }) : [];
  // #2288: the choice row shows for a new payee set to Income, until a pick.
  const showProviderChoice = Boolean(onProviderChoice && prompt?.mode === 'new' && value === 'income');
  const pulseName = flashSeq % 2 ? 'brEntryTypePulseA' : 'brEntryTypePulseB';
  const pulseFrames = {
    '0%, 100%': { outlineColor: 'transparent' },
    '50%': { outlineColor: palette.interactive },
  };
  const flashSx = (count, delayMs) => ({
    outline: '2px solid transparent',
    outlineOffset: '0px',
    '@keyframes brEntryTypePulseA': pulseFrames,
    '@keyframes brEntryTypePulseB': pulseFrames,
    ...(flashSeq ? { animation: `${pulseName} ${FLASH_PULSE_MS}ms ${ds.motion.easeOut} ${delayMs}ms ${count} both` } : {}),
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      outlineColor: flashing ? palette.interactive : 'transparent',
    },
  });
  const rootProps = {
    ref: setRoot,
    'data-entry-type-control': 'true',
    'data-pending': pending ? 'true' : undefined,
    'data-flash': flashSeq || undefined,
    ...(keepFocus ? { onMouseDown: (e) => e.preventDefault() } : {}),
  };
  const choiceButtonSx = (delayMs) => ({
    textTransform: 'none',
    fontSize: '12px',
    lineHeight: 1.2,
    padding: '4px 10px',
    borderRadius: `${ds.radius.button}px`,
    borderColor: palette.borderStrong,
    color: palette.text.primary,
    '&:hover': { borderColor: palette.interactive, backgroundColor: palette.surface.hover },
    ...flashSx(3, delayMs),
  });

  const body = (
    <Box data-testid="entry-type-control" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <EntryTypeHelp label={t['entryType.label']}>
        <Segmented
          inCard
          ariaLabel={t['entryType.label']}
          options={entryTypeOptions(t, { incomeAllowed })}
          value={value}
          previewValue={preview}
          onPreview={setPreview}
          onChange={onChange}
        />
      </EntryTypeHelp>
      {segments.length > 0 ? (
        <Box data-testid="entry-type-caption" sx={{ fontSize: '11px', lineHeight: 1.35, color: palette.text.secondary, maxWidth: 420 }}>
          {segments.map((seg, i) => (seg.manage ? (
            <Link key={i} href={MANAGE_PAYEES_PATH} target="_blank" rel="noopener" underline="hover">{seg.text}</Link>
          ) : (
            <span key={i}>{seg.text}</span>
          )))}
        </Box>
      ) : null}
      {!incomeAllowed ? (
        <Box data-testid="entry-type-card-note" sx={{ fontSize: '11px', lineHeight: 1.35, color: palette.text.secondary, maxWidth: 420 }}>
          {t['entryType.noIncomeOnCard']}
        </Box>
      ) : null}
      {showProviderChoice ? (
        <Box data-testid="entry-type-provider-choice" role="group" aria-label={interpolate(t['entryType.provider.question'], { payee: prompt.payeeName })} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '4px', maxWidth: 420 }}>
          <Box sx={{ fontSize: '12px', fontWeight: ds.font.weightMedium, color: palette.text.primary }}>
            {interpolate(t['entryType.provider.question'], { payee: prompt.payeeName })}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              data-testid="entry-type-provider-default"
              onClick={() => onProviderChoice('all')}
              sx={choiceButtonSx(FLASH_LEFT_DELAY_MS)}
            >
              {t['entryType.provider.default']}
            </Button>
            <Button
              variant="outlined"
              size="small"
              data-testid="entry-type-provider-source"
              onClick={() => onProviderChoice('provider')}
              sx={choiceButtonSx(FLASH_RIGHT_DELAY_MS)}
            >
              {t['entryType.provider.source']}
            </Button>
          </Box>
          <Box data-testid="entry-type-provider-caption" sx={{ fontSize: '11px', lineHeight: 1.35, color: palette.text.secondary }}>
            {t['entryType.provider.caption']}
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  if (floating) {
    const side = placement === 'side';
    return (
      <Popper
        open={visible}
        anchorEl={anchorEl}
        placement={side ? 'right' : 'bottom-start'}
        disablePortal={!side}
        popperOptions={{ strategy: 'fixed' }}
        modifiers={side ? [
          // Beside the highlighted option, clear of the list's edge; on the
          // left when the right has no room.
          { name: 'offset', options: { offset: [0, 10] } },
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { fallbackPlacements: ['left', 'bottom-start'] } },
        ] : [
          // Below the line being entered, clear of the row, over the saved rows.
          { name: 'offset', options: { offset: [0, 18] } },
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        ]}
        sx={{ zIndex: side ? theme.zIndex.modal + 1 : theme.zIndex.fab }}
      >
        <Paper
          {...rootProps}
          sx={{
            backgroundColor: palette.surface.overlay,
            backgroundImage: 'none',
            border: `1px solid ${palette.overlay.border}`,
            boxShadow: palette.overlay.shadow,
            borderRadius: `${ds.radius.container}px`,
            padding: '6px 10px 8px',
            ...flashSx(2, 0),
          }}
        >
          {body}
        </Paper>
      </Popper>
    );
  }

  if (!visible) return null;
  return (
    <Box {...rootProps} sx={{ paddingTop: '2px', borderRadius: `${ds.radius.container}px`, ...flashSx(2, 0), ...sx }}>
      {body}
    </Box>
  );
};

export default EntryTypeControl;
